import type { TestFeedbackFontSchemaType } from '@vedalib/editor/lib/brand'
import type { TestAnswerFeedbackSchemaType } from '@vedalib/editor/lib/brand'
import type { ElementValue, FeedbackRTValue } from '@vedalib/editor/lib/elements'
import { TestAnswerFeedbackType } from '@vedalib/editor/lib/elements'
import { feedbackValue } from '@vedalib/editor/lib/elements/constants'
import type { RichTextValue } from '@vedalib/rich-text'
import { SlateElementType, textToRtValue, rtValueToText } from '@vedalib/rich-text'
import { useUpdateEffect } from 'ahooks'
import * as R from 'ramda'
import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import RichText from 'components/form/RichText/RichText'
import type { ElementFeedbackCss, ElementFontCss } from 'services/Branding/types'
import ScrollService from 'services/Scroll/ScrollService'
import { ScrollContainerEnum } from 'services/Scroll/enums'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getIsInteractiveMode } from 'services/Store/Project/selectors'
import type { IBlockMode, IBlockResult } from 'services/Store/Project/types'
import { testProps } from 'utils/test/qaData'

import type { RTFeedbackValue, TypeFb } from '../TestFeedback.types'
import failIcon from '../assets/fail.svg'
import infoIcon from '../assets/info.svg'
import successIcon from '../assets/success.svg'
import FeedbackStyled from '../styled/FeedbackStyled'
import * as s from './Feedback.module.scss'

interface IFeedbackProps {
  mode?: IBlockMode
  isTrueAnswer?: boolean
  font: ElementFontCss<ElementValue>
  onChange?: (newFeedbackValue: FeedbackRTValue) => void
  typeFb?: TestAnswerFeedbackType
  rtProps?: RTFeedbackValue
  styles: ElementFeedbackCss<TestAnswerFeedbackSchemaType>
  type: TypeFb
  blockResult?: IBlockResult
  id?: string
}

const RT_MAP = {
  [SlateElementType.elementDefault]: 'base',
  [SlateElementType.heading1]: 'h1',
  [SlateElementType.heading2]: 'h2',
  [SlateElementType.heading3]: 'h3',
  [SlateElementType.heading4]: 'h4',
  [SlateElementType.caption]: 'caption',
}
const ICONS_MAP = { fail: failIcon, info: infoIcon, success: successIcon }
const ANIMATION_TIME = 500
const TRANSITION = `max-height ${ANIMATION_TIME}ms ease-in-out`

const getFbView = (
  type: 'info' | 'success' | 'fail',
  font: ElementFontCss<TestFeedbackFontSchemaType>,
  styles: ElementFeedbackCss<TestAnswerFeedbackSchemaType>,
  value?: FeedbackRTValue,
) => {
  const text = value ? value[type] : textToRtValue('')
  const iconPath = (styles && styles[type]?.backgroundImage) || `url(${ICONS_MAP[type]})`
  // @ts-expect-error TODO
  const firstRow = value ? font?.[RT_MAP?.[value[type]?.[0]?.type] || 'base'] : font?.base
  const height = `calc(${parseInt(firstRow?.lineHeight) || 1.2} * ${parseInt(firstRow?.fontSize) || 16}px)`
  return {
    text,
    stylesIcon: {
      backgroundImage: iconPath,
      height,
    },
  }
}

const Feedback = ({
  mode,
  isTrueAnswer,
  styles,
  font,
  typeFb,
  onChange,
  rtProps,
  type: typeField,
  blockResult,
  id,
}: IFeedbackProps) => {
  const isPreview = useProjectContext(getIsInteractiveMode)
  const isDemoMode = mode?.previewMode === PreviewMode.demo
  const ref = useRef<HTMLDivElement>(null)
  const [maxHeight, setMaxHeight] = useState<number | string>(isPreview ? 0 : 'none')
  const type = typeFb === TestAnswerFeedbackType.info ? 'info' : isTrueAnswer ? 'success' : 'fail'
  const elementValue = rtProps?.value || feedbackValue
  const hasIcon = styles?.common.displayIcon

  const view = useMemo(
    () => getFbView(type, font, styles, elementValue),
    [elementValue, styles, font, type],
  )

  const onChangeLabel = (labelValue: RichTextValue) => {
    onChange?.(R.assoc(type, labelValue, elementValue))
  }

  useEffect(() => {
    if (isPreview) {
      if (ref.current && (blockResult || isDemoMode)) {
        setMaxHeight(ref.current.offsetHeight)
      }
    } else {
      setMaxHeight(isPreview ? 0 : 'none')
    }
  }, [isPreview, blockResult, mode, view, isDemoMode])

  useUpdateEffect(() => {
    if (blockResult && ref.current) {
      ScrollService.scroll(
        {
          container: ScrollContainerEnum.canvas,
          el: ref.current,
          block: 'begin',
          scroll: 'always',
          async: ANIMATION_TIME,
        },
        null,
      )
    }
  }, [blockResult])

  const textName = `${typeField}.${type}`

  if (!isPreview || blockResult || mode?.previewMode === PreviewMode.demo) {
    return (
      <FeedbackStyled $styles={styles} $type={type} $typeField={typeField}>
        <div
          className={s.wrapper}
          // Пропадает текст обратной связи (Windows | webkit)
          // https://www.notion.so/BUG-4050-32d5457f94c580debd9efeb1749b97fb
          onTransitionEnd={(e) => {
            if (e.target instanceof HTMLDivElement) {
              e.target.style.overflow = 'visible'
            }
          }}
          style={{
            ...(styles?.effects as CSSProperties),
            borderRadius: styles.common?.borderRadius as CSSProperties['borderRadius'],
            transition: isDemoMode ? 'none' : TRANSITION,
            maxHeight,
          }}
        >
          <div className={s.root} ref={ref}>
            {hasIcon && (
              <span
                {...testProps({ el: 'iconFeedback', type })}
                className={s.icon}
                style={view?.stylesIcon}
                aria-hidden
              />
            )}
            <div
              {...testProps({ el: 'titleFeedback', label: rtValueToText(view?.text) })}
              className={s.text}
            >
              <span
                id={id}
                onMouseDown={() => rtProps?.onLabelSelect && rtProps?.onLabelSelect(textName)}
              >
                <RichText
                  active={(rtProps?.isActiveElement && rtProps?.activeIndex === textName) || false}
                  disabled={!rtProps?.isFill}
                  isPreview={mode?.previewMode !== PreviewMode.editor}
                  name={textName}
                  onChange={(val) => onChangeLabel(val)}
                  styles={font}
                  value={view?.text || textToRtValue('')}
                />
              </span>
            </div>
          </div>
        </div>
      </FeedbackStyled>
    )
  }

  return null
}

export default Feedback
